import type { SpeedEventType, UUID } from '@cobliteam/common-types';
import moment from 'moment';

import type { Configuration, ReportFormat } from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import TimeZone from '../../request/Timezone';
import type { IRequest } from '../../request/Types';

export type ReportType = 'periodical' | 'daily';

export class AnalyticsReportRequester {
  private requester: Requester;

  private baseUrl: string;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
    this.baseUrl = '/analytics/v1';
  }

  public requestVehicleIdleEngineReportToEmail = async (
    startTimestamp: number,
    endTimestamp: number,
    reportType: ReportType,
    destinationEmails: string[],
    vehicleIds?: UUID[],
    idleEngineThresholdInMinutes?: number
  ): Promise<string> => {
    const timeZone = TimeZone.getTimezone();

    const requestBody = {
      start_date: moment(startTimestamp).toISOString(true),
      end_date: moment(endTimestamp).toISOString(true),
      timezone: timeZone,
      report_type: reportType,
      destination_emails: destinationEmails,
      vehicle_ids: vehicleIds,
      idle_engine_threshold_in_minutes: idleEngineThresholdInMinutes,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/idle-engine/vehicle/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestData);

    // @ts-ignore
    return apiData.data;
  };

  public requestVehicleIdleEngineEventsReportToEmail = async (
    startTimestamp: number,
    endTimestamp: number,
    vehicleId: UUID,
    destinationEmails: string[],
    idleEngineThresholdInMinutes?: number
  ): Promise<string> => {
    const timeZone = TimeZone.getTimezone();

    const requestBody = {
      start_date: moment(startTimestamp).toISOString(true),
      end_date: moment(endTimestamp).toISOString(true),
      timezone: timeZone,
      vehicle_id: vehicleId,
      destination_emails: destinationEmails,
      idle_engine_threshold_in_minutes: idleEngineThresholdInMinutes,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/idle-engine/vehicle/events/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestData);

    // @ts-ignore
    return apiData.data;
  };

  public requestDriverIdleEngineReportToEmail = async (
    startTimestamp: number,
    endTimestamp: number,
    reportType: ReportType,
    destinationEmails: string[],
    driverIds?: UUID[],
    idleEngineThresholdInMinutes?: number
  ): Promise<string> => {
    const timeZone = TimeZone.getTimezone();

    const requestBody = {
      start_date: moment(startTimestamp).toISOString(true),
      end_date: moment(endTimestamp).toISOString(true),
      timezone: timeZone,
      report_type: reportType,
      destination_emails: destinationEmails,
      driver_ids: driverIds,
      idle_engine_threshold_in_minutes: idleEngineThresholdInMinutes,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/idle-engine/driver/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestData);

    // @ts-ignore
    return apiData.data;
  };

  public requestDriverVehicleAssociationReportByVehicleToEmail = async (startDate: number, endDate: number, destinationEmails: string[], vehicleIds?: UUID[]): Promise<string> => {
    const timeZone = TimeZone.getTimezone();

    const requestBody = {
      start_date: moment(startDate).toISOString(true),
      end_date: moment(endDate).toISOString(true),
      timezone: timeZone,
      destination_emails: destinationEmails,
      vehicle_ids: vehicleIds,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/driver-vehicle-association/vehicle/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestData);

    // @ts-ignore
    return apiData.data;
  };

  public requestDriverVehicleAssociationReportByDriverToEmail = async (startDate: number, endDate: number, destinationEmails: string[], driverIds?: UUID[]): Promise<string> => {
    const timeZone = TimeZone.getTimezone();

    const requestBody = {
      start_date: moment(startDate).toISOString(true),
      end_date: moment(endDate).toISOString(true),
      timezone: timeZone,
      driver_ids: driverIds,
      destination_emails: destinationEmails,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/driver-vehicle-association/driver/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestData);

    // @ts-ignore
    return apiData.data;
  };

  public requestDriverIdleEngineEventsReportToEmail = async (
    startTimestamp: number,
    endTimestamp: number,
    driverId: UUID,
    destinationEmails: string[],
    idleEngineThresholdInMinutes?: number
  ): Promise<string> => {
    const timeZone = TimeZone.getTimezone();

    const requestBody = {
      start_date: moment(startTimestamp).toISOString(true),
      end_date: moment(endTimestamp).toISOString(true),
      timezone: timeZone,
      driver_id: driverId,
      destination_emails: destinationEmails,
      idle_engine_threshold_in_minutes: idleEngineThresholdInMinutes,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/idle-engine/driver/events/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestData);

    // @ts-ignore
    return apiData.data;
  };

  public requestGeofenceEventsReportToEmail = async (
    startTimestamp: number,
    endTimestamp: number,
    destinationEmails: string[],
    vehicleIds?: UUID[],
    groupIds?: UUID[],
    geofenceIds?: UUID[],
    geofenceTypes?: string[],
    minDurationInSeconds?: number,
    hourBegin?: number,
    hourEnd?: number
  ): Promise<string> => {
    const timeZone = TimeZone.getTimezone();

    const requestBody = {
      start_time: moment(startTimestamp).toISOString(true),
      end_time: moment(endTimestamp).toISOString(true),
      timezone: timeZone,
      destination_emails: destinationEmails,
      vehicle_ids: vehicleIds,
      group_ids: groupIds,
      geofence_ids: geofenceIds,
      geofence_types: geofenceTypes,
      min_duration_in_seconds: minDurationInSeconds,
      hour_begin: hourBegin,
      hour_end: hourEnd,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/geofence/events/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestData);

    // @ts-ignore
    return apiData.data;
  };

  public requestDriverSafetyReportToEmail = async (
    startTimestamp: number,
    endTimestamp: number,
    destinationEmails: string[],
    reportFormat: ReportFormat,
    speedEventType: SpeedEventType,
    driverIds: UUID[] = []
  ): Promise<string> => {
    const timeZone = TimeZone.getTimezone();

    const requestBody = {
      start_date: moment(startTimestamp).toISOString(true),
      end_date: moment(endTimestamp).toISOString(true),
      timezone: timeZone,
      destination_emails: destinationEmails,
      driver_ids: driverIds,
      report_format: reportFormat,
      speed_event_type: speedEventType,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/driver-safety/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestData);

    // @ts-ignore
    return apiData.data;
  };

  public requestDriverProductivityToEmail = async (startDate: number, endDate: number, driverIds: UUID[], reportType: ReportType, destinationEmails: string[]): Promise<string> => {
    const timeZone = TimeZone.getTimezone();

    const requestBody = {
      start_date: moment(startDate).toISOString(true),
      end_date: moment(endDate).toISOString(true),
      timezone: timeZone,
      destination_emails: destinationEmails,
      driver_ids: driverIds,
      report_type: reportType,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/productivity/driver/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestData);

    // @ts-ignore
    return apiData.data;
  };

  public requestVehicleProductivityToEmail = async (
    startDate: number,
    endDate: number,
    vehicleIds: UUID[],
    reportType: ReportType,
    destinationEmails: string[]
  ): Promise<string> => {
    const timeZone = TimeZone.getTimezone();

    const requestBody = {
      start_date: moment(startDate).toISOString(true),
      end_date: moment(endDate).toISOString(true),
      timezone: timeZone,
      destination_emails: destinationEmails,
      vehicle_ids: vehicleIds,
      report_type: reportType,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/productivity/vehicle/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestData);

    // @ts-ignore
    return apiData.data;
  };

  public requestTriggerEventsReportToEmail = async (
    startTimestamp: number,
    endTimestamp: number,
    destinationEmails: string[],
    ruleId?: UUID,
    ruleName?: string,
    vehicleGroupIds?: UUID[],
    vehicleIds?: UUID[],
    geofenceIds?: UUID[],
    conditionIds?: UUID[],
    eventId?: UUID
  ): Promise<string> => {
    const timezone = TimeZone.getTimezone();

    const requestBody = {
      start_date: moment(startTimestamp).toISOString(true),
      end_date: moment(endTimestamp).toISOString(true),
      timezone,
      destination_emails: destinationEmails,
      ...(ruleId && { rule_id: ruleId }),
      ...(eventId && { event_id: eventId }),
      name: ruleName,
      vehicle_group_ids: vehicleGroupIds,
      vehicle_ids: vehicleIds,
      geofence_ids: geofenceIds,
      condition_ids: conditionIds,
    };

    const requestDate: IRequest = {
      endpoint: `${this.baseUrl}/triggers/events/report/mail`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    const apiData = await this.requester.request(requestDate);

    return apiData.data;
  };

  public requestSpeedingPathReport = async (startTimestamp: number, endTimestamp: number, vehicleId: string, reportFormat: string): Promise<Blob> => {
    const begin = moment(startTimestamp).toISOString(true);
    const end = moment(endTimestamp).toISOString(true);
    const timeZone = TimeZone.getTimezone();

    const requestData: IRequest = {
      endpoint:
        `${this.baseUrl}/speeding-path/report?` + `start_date=${begin}` + `&end_date=${end}` + `&timezone=${timeZone}` + `&format=${reportFormat}` + `&vehicle_id=${vehicleId}`,
      method: HttpMethod.GET,
      responseType: 'arraybuffer',
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data]));
  };
}

export default AnalyticsReportRequester;
